import { Modal, RadioButton } from '@everlywell/leaves';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import DisclaimerModal from 'components/DisclaimerModal';
import { CartContext } from 'components/Layout';
import StripePayment from 'components/Stripe/StripePayment';
import parse from 'html-react-parser';
import React, { useContext, useEffect, useRef, useState } from 'react';
import {
  CART_EVENTS_NAMES,
  currentOrderGrandTotalInCents,
} from 'utils/cartHelpers';
import { ANALYTICS } from 'utils/constants/analytics';
import { generateKey } from 'utils/helpers';
import { isCovidOnCoreCheckout } from 'utils/helpers/covid';
import { stripePublicKey } from 'utils/helpers/paymentHelpers';
import { ProductInfoContext } from 'utils/helpers/pdpContexts';
import { CreditRedemptionModalContext } from 'utils/hooks/useCreditRedemptionModal/context';

import {
  SUBSCRIBE,
  SubscriptionOptionType,
} from '../../../../containers/PDP/HeroContainer';
import * as S from './PurchaseControls.styles';

const stripePromise = loadStripe(stripePublicKey);

const COVID_SLUG = 'covid-19-test';
const DEFAULT_QUANTITY_OPTIONS = 9;

export type PurchaseTypeRadioButton = {
  label: string;
  name: string;
};

export type PurchaseControlsProps = {
  onPurchaseTypeChange: (e: React.FormEvent<HTMLInputElement>) => void;
  onSubscriptionOptionChange: (e: React.FormEvent<HTMLInputElement>) => void;
  onAddToCartClick: (
    e: React.FormEvent<HTMLButtonElement>,
    callback?: () => void,
  ) => void;
  onQuantityPickerChange: (e: React.FormEvent<HTMLInputElement>) => void;
  purchaseTypeRadioButtons: PurchaseTypeRadioButton[];
  selectedPurchaseType: string;
  selectedSubscriptionOption: SubscriptionOptionType;
  subscriptionOptions: {
    label: string;
    name: string;
    savingsStatement: string;
  }[];
  activeCredits?: number;
  membershipVariantId: number;
  isStickyAppearance?: boolean;
  hideSubscription?: boolean;
  heroSubscriptionFaqUrl?: string;
  onFrequencyTriggerClick?: () => void;
  onExpressPayClick: (cb: () => void) => void;
  stripeTotal: string;
  ctaSubheadline: string;
  quantity: number;
  maxQuantity?: number;
};

export const PurchaseControls = ({
  onPurchaseTypeChange,
  onSubscriptionOptionChange,
  purchaseTypeRadioButtons,
  selectedPurchaseType,
  selectedSubscriptionOption,
  subscriptionOptions,
  onQuantityPickerChange,
  onAddToCartClick,
  activeCredits,
  membershipVariantId,
  isStickyAppearance = false,
  hideSubscription,
  heroSubscriptionFaqUrl,
  onFrequencyTriggerClick,
  onExpressPayClick,
  stripeTotal,
  ctaSubheadline,
  quantity,
  maxQuantity,
}: PurchaseControlsProps): JSX.Element => {
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [isDisabled, setIsDisabled] = useState<boolean>(true);
  const [openDisclaimerModal, setOpenDisclaimerModal] =
    useState<boolean>(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [showAltPayment, setShowAltPayment] = useState(false);
  const { setTOS, isCartLoaded } = useContext(CartContext);
  const { slug, sku, collectionMethods } = useContext(ProductInfoContext);
  const { openCreditRedemptionModal } = useContext(
    CreditRedemptionModalContext,
  );
  const orderButtonRef = useRef(null);
  const handleChange = (e: React.FormEvent<HTMLInputElement>) => {
    onSubscriptionOptionChange(e);
  };
  const isCovid = slug === COVID_SLUG;

  useEffect(() => {
    setIsDisabled(!isCartLoaded);
  }, [isCartLoaded]);

  const handleModalAddToCartClick = (e: React.FormEvent<HTMLButtonElement>) => {
    onAddToCartClick(e);
    setOpenModal(false);
  };

  const handleFrequencyTriggerClick = () => {
    onFrequencyTriggerClick && onFrequencyTriggerClick();
    setOpenModal(true);
  };

  const handleAddToCartClick = (e: React.FormEvent<HTMLButtonElement>) => {
    if (isCovid) {
      setOpenDisclaimerModal(true);
      // the Disclaimer Modal opens and will have the continue button to redirect to checkout
    } else {
      setIsDisabled(true);
      const addToCartCallback = () => setIsDisabled(false);
      onAddToCartClick(e, addToCartCallback);
    }
  };

  const handleCartLoading = (event: Event) => {
    const {
      detail: { isLoading },
    } = event as CustomEvent;

    setIsDisabled(isLoading);
  };

  const ctaText = isCovid && !isCovidOnCoreCheckout() ? 'Order' : 'Add to Cart';

  // Quantity dropdown will show a number of options as specified
  // on the given product, up to the default (set above)
  const quantityOptions = maxQuantity
    ? Math.min(maxQuantity, DEFAULT_QUANTITY_OPTIONS)
    : DEFAULT_QUANTITY_OPTIONS;

  useEffect(() => setHasLoaded(true), []);

  // ---REMOVE hasLabVisit LOGIC WHEN EXPRESS PAY IN BDC CARTS IS FIXED (below this line)---
  const hasLabVisit =
    collectionMethods &&
    collectionMethods.length > 0 &&
    Object.values(collectionMethods).some(
      (method) => method.v2Name === 'Blood draw at a Quest location',
    );

  const showAltPaymentButtons = hasLoaded && !isCovid && !hasLabVisit;

  useEffect(
    () => setShowAltPayment(showAltPaymentButtons),
    [showAltPaymentButtons],
  );
  // ---REMOVE hasLabVisit LOGIC WHEN EXPRESS PAY IN BDC CARTS IS FIXED (above this line)---

  useEffect(() => {
    window.addEventListener(CART_EVENTS_NAMES.loading, handleCartLoading);

    return () => {
      window.removeEventListener(CART_EVENTS_NAMES.loading, handleCartLoading);
    };
  }, []);
  return (
    <>
      {!isStickyAppearance && !hideSubscription && (
        <S.Fieldset>
          <S.Legend
            data-testid="purchase-control-legend"
            hideSubscription={hideSubscription}
          >
            one time purchase or subscribe
          </S.Legend>
          <S.ButtonsWrapper hideSubscription={hideSubscription}>
            {selectedSubscriptionOption &&
              purchaseTypeRadioButtons.map((button) => (
                <S.RadioWrapper key={generateKey()}>
                  <RadioButton
                    id={button.name}
                    name={button.name}
                    label={
                      button.name === SUBSCRIBE
                        ? `${selectedSubscriptionOption.name} Subscription (Save ${selectedSubscriptionOption.percentDiscount}%)`
                        : button.label
                    }
                    onChange={onPurchaseTypeChange}
                    checked={selectedPurchaseType === button.name}
                  />
                  {button.name === SUBSCRIBE &&
                    (subscriptionOptions.length > 1 ? (
                      <>
                        <S.FrequencyTrigger
                          onClick={handleFrequencyTriggerClick}
                          data-testid="frequencyModalTrigger"
                        >
                          Change Frequency
                        </S.FrequencyTrigger>
                        {openModal && (
                          <Modal open={openModal} openHandler={setOpenModal}>
                            <S.ModalContent>
                              <S.SubscriptionModalHeader>
                                Select Subscription Frequency
                              </S.SubscriptionModalHeader>
                              <S.FieldsetWrapper>
                                <S.SubscriptionFieldset>
                                  {subscriptionOptions.map((option) => (
                                    <S.SubscriptionOption key={generateKey()}>
                                      <RadioButton
                                        id={option.name}
                                        data-testid="subscriptionOption"
                                        name={option.name}
                                        label={option.label}
                                        onChange={handleChange}
                                        checked={
                                          selectedSubscriptionOption.name ===
                                          option.name
                                        }
                                      />
                                      <S.Savings>
                                        {option.savingsStatement}
                                      </S.Savings>
                                    </S.SubscriptionOption>
                                  ))}
                                </S.SubscriptionFieldset>
                              </S.FieldsetWrapper>

                              <S.ModalCTAWrapper>
                                <S.Gradient />
                                <S.CTA
                                  appearance="hero"
                                  onClick={handleModalAddToCartClick}
                                  data-testid="addToCartButton"
                                  data-name={ANALYTICS.LABELS.ADD_TO_CART_MODAL}
                                  data-purchase-type={selectedPurchaseType}
                                  data-sku={sku}
                                  isDisabled={isDisabled}
                                  isLoading={isDisabled}
                                >
                                  Add to Cart
                                </S.CTA>
                                <S.CTASubheadline>
                                  {ctaSubheadline}
                                </S.CTASubheadline>
                              </S.ModalCTAWrapper>
                            </S.ModalContent>
                          </Modal>
                        )}
                      </>
                    ) : (
                      heroSubscriptionFaqUrl && (
                        <S.SubscriptionLink
                          href={heroSubscriptionFaqUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Learn More About Subscriptions
                        </S.SubscriptionLink>
                      )
                    ))}
                </S.RadioWrapper>
              ))}
          </S.ButtonsWrapper>
        </S.Fieldset>
      )}
      {isStickyAppearance && (
        <S.StickyPurchaseWrapper>
          <S.StickyMobileAddToCart
            appearance="hero"
            onClick={handleAddToCartClick}
            data-testid="stickyMobileAddToCartButton"
            data-purchase-type={selectedPurchaseType}
            data-sku={sku}
            isDisabled={isDisabled}
          >
            {ctaText}
          </S.StickyMobileAddToCart>
        </S.StickyPurchaseWrapper>
      )}
      <S.DesktopCTAWrapper isStickyAppearance={isStickyAppearance}>
        <S.ButtonAndQtyPicker>
          {activeCredits && selectedPurchaseType !== SUBSCRIBE ? (
            <S.CTA
              appearance="hero"
              onClick={() => openCreditRedemptionModal(membershipVariantId)}
              data-testid="orderMemberTestButton"
              data-purchase-type={selectedPurchaseType}
              data-sku={sku}
              ref={orderButtonRef}
            >
              Order
            </S.CTA>
          ) : (
            <>
              <S.QuantityPicker
                onQuantityPickerChange={onQuantityPickerChange}
                numOfOptions={quantityOptions}
                selectedOption={quantity}
              />
              <S.CTA
                appearance="hero"
                onClick={handleAddToCartClick}
                data-testid="addToCartButton"
                data-purchase-type={selectedPurchaseType}
                data-sku={sku}
                isDisabled={isDisabled}
                isLoading={isDisabled}
              >
                {ctaText}
              </S.CTA>
              {showAltPayment && (
                <Elements stripe={stripePromise}>
                  <StripePayment
                    cartTotal={currentOrderGrandTotalInCents(+stripeTotal)}
                    setTOS={setTOS}
                    onPaymentConfirm={onExpressPayClick}
                    cartNotEmpty={true}
                    optionType="default"
                    optionTheme="light-outline"
                    label={ANALYTICS.LABELS.EXPRESS_PAY_PDP}
                  />
                </Elements>
              )}
            </>
          )}
        </S.ButtonAndQtyPicker>
        {!isStickyAppearance && (
          <S.CTASubheadline data-testid="ctaSubheadline">
            {parse(ctaSubheadline || '')}
          </S.CTASubheadline>
        )}
      </S.DesktopCTAWrapper>
      <DisclaimerModal
        open={openDisclaimerModal}
        openHandler={setOpenDisclaimerModal}
        productSku={sku}
        selectedQuantity={quantity}
        handleAddToCart={onAddToCartClick}
      />
    </>
  );
};

export default PurchaseControls;
